import { NONE_ANALYSIS, STUDY_DESIGN } from '../../data/conjointData';
import { formatPercent, formatNumber } from '../../lib/formatters';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { CHART_COLORS, TOOLTIP_STYLE } from '../../styles/chart-theme';
import { InfoButton } from '../common/Tooltip';
import { useIsMobile } from '../../hooks/useIsMobile';

export function NoneAnalysisPanel() {
  const isMobile = useIsMobile();
  const { overallNoneRate, alwaysNoneCount, alwaysNonePercent, neverNoneCount, neverNonePercent, addressableMarketCount, addressableMarketPercent } = NONE_ANALYSIS;
  const sometimesNone = STUDY_DESIGN.totalRespondents - alwaysNoneCount - neverNoneCount;

  const donutData = [
    { name: 'Always None', value: alwaysNoneCount, color: CHART_COLORS.red },
    { name: 'Sometimes None', value: sometimesNone, color: CHART_COLORS.amber },
    { name: 'Never None', value: neverNoneCount, color: CHART_COLORS.emerald },
  ];

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">None Analysis</h3>
        <InfoButton helpKey="noneAnalysis" size={13} />
      </div>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <ResponsiveContainer width="100%" height={isMobile ? 160 : 200}>
            <PieChart>
              <Pie data={donutData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" strokeWidth={2} stroke="var(--color-dot-stroke)">
                {donutData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip {...TOOLTIP_STYLE} formatter={(value: number, name: string) => [`${formatNumber(value)} (${formatPercent(value / STUDY_DESIGN.totalRespondents)})`, name]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 text-xs mt-2">
            {donutData.map(d => (
              <span key={d.name} className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />{d.name}
              </span>
            ))}
          </div>
        </div>
        <div className="space-y-4">
          <StatRow label="Overall None Rate" helpKey="noneAnalysis" value={formatPercent(overallNoneRate)} desc="Of all choice tasks, respondents chose 'none'" />
          <StatRow label="Always-None" helpKey="alwaysNone" value={`${formatNumber(alwaysNoneCount)} (${formatPercent(alwaysNonePercent)})`} desc="Chose 'none' in every single task — truly unreachable" color="text-metric-negative" />
          <StatRow label="Never-None" helpKey="neverNone" value={`${formatNumber(neverNoneCount)} (${formatPercent(neverNonePercent)})`} desc="Never chose 'none' — always in-market" color="text-metric-positive" />
          <StatRow label="Addressable Market" helpKey="addressableMarket" value={`${formatNumber(addressableMarketCount)} (${formatPercent(addressableMarketPercent)})`} desc="Respondents who could potentially be converted" color="text-chart-teal" />
        </div>
      </div>
    </div>
  );
}

function StatRow({ label, helpKey, value, desc, color = 'text-primary' }: { label: string; helpKey?: string; value: string; desc: string; color?: string }) {
  return (
    <div>
      <div className="text-xs text-muted uppercase tracking-wider flex items-center gap-1">{label}{helpKey && <InfoButton helpKey={helpKey} size={10} />}</div>
      <div className={`font-mono text-sm font-semibold ${color}`}>{value}</div>
      <div className="text-xs text-muted mt-0.5">{desc}</div>
    </div>
  );
}
